import { useEffect, useState } from 'react';
import Image from 'next/image';
import { useLockedBody } from 'usehooks-ts';
import { Link } from '~/components/global';
import logoDark from '../../public/as-appsumo-logo-dark.svg';
import logoLight from '../../public/as-appsumo-logo-light.svg';
import NavControls from './components/NavControls';
import NavGroups from './components/NavGroups';
import NavPromo from './components/NavPromo';
import NavUser from './components/NavUser';
import SearchInput from './components/SearchInput';
import SellOnAppsumo from './components/SellOnAppsumo';
import PartnerPortal from './components/PartnerPortal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useRouter } from 'next/router';
import clsx from 'clsx';
import useUser from '~/lib/user';
import { useWindowSize } from 'usehooks-ts';

export default function NavHeader({
  isCollectionBrowsePage,
}: {
  isCollectionBrowsePage?: boolean;
}) {
  const router = useRouter();
  const { user } = useUser();
  const isAlternativeTo =
    router.asPath.indexOf('/collections/alternative/') === 0;

  const [showMobileMenu, setShowMobileMenu] = useLockedBody(false, 'root');

  const handleMenuToggle = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  const { height } = useWindowSize();
  const [vh, setVh] = useState<string>(`${height || 0}px`);

  useEffect(() => setVh(`${height}px`), [height]);

  useEffect(() => {
    const handleRouteChange = () => {
      setShowMobileMenu(false);
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router.events, setShowMobileMenu]);

  let darkLogoClass = 'hidden';
  let lightLogoClass = '';

  if (isAlternativeTo) {
    darkLogoClass = 'md:hidden';
    lightLogoClass = 'hidden md:block';
  }

  return (
    <div
      id="header"
      className={clsx(
        'fixed left-0 top-0 z-10 w-full',
        'before:fixed before:left-0 before:top-0 before:-z-1 before:h-screen before:w-screen before:bg-black',
        'before:transition-opacity before:duration-300 before:ease-in-out before:content-[""]',
        showMobileMenu
          ? 'before:visible before:opacity-30 '
          : 'before:invisible before:opacity-0',
      )}
    >
      <div
        style={
          {
            '--vh': `${vh}`,
          } as React.CSSProperties
        }
        className={clsx(
          'z-20 w-full border-b border-navbar bg-navbar text-navbar',
          'mx-auto max-w-screen-4xl px-8 pt-[10px] md:pt-[18px]',
          showMobileMenu ? 'max-h-[var(--vh)] overflow-y-scroll' : '',
          isAlternativeTo ? 'md:dark' : '',
        )}
      >
        <nav
          className={clsx(
            'grid grid-cols-[[full-start]_20%_1fr_20%_[full-end]] items-center md:mt-0 md:gap-y-[10px]',
            'md:grid-cols-[[full-start]_min-content_1fr_min-content_min-content_[full-end]]',
          )}
        >
          <div className="flex md:hidden">
            <button onClick={handleMenuToggle}>
              <span className="sr-only">Open menu</span>
              <FontAwesomeIcon
                icon={showMobileMenu ? faTimes : faBars}
                width={20}
                height={20}
              />
            </button>
          </div>
          <div>
            <Link
              href="/"
              className="mx-auto flex h-[48px] w-[100px] items-center md:w-[150px]"
            >
              <span className="sr-only">AppSumo</span>
              <Image
                alt="AppSumo"
                src={logoLight}
                width="150"
                height="23"
                className={darkLogoClass}
              />
              <Image
                alt="AppSumo"
                src={logoDark}
                width="150"
                height="23"
                className={lightLogoClass}
              />
            </Link>
          </div>
          <SearchInput isCollectionBrowsePage={isCollectionBrowsePage} />
          <div
            className={clsx(
              'order-5 col-[full] mx-[-30px] mt-[-15px] items-center justify-end bg-gray-100 px-[30px] py-[15px] max-md:shadow-inner',
              'md:order-none md:col-auto md:m-0 md:flex md:bg-transparent md:p-0',
              { block: showMobileMenu },
              { hidden: !showMobileMenu },
            )}
          >
            {user?.has_partner_portal ? <PartnerPortal /> : <SellOnAppsumo />}
            <NavUser />
          </div>
          <NavControls />
          <div
            className={clsx(
              { hidden: !showMobileMenu },
              'order-4 h-auto',
              'col-[full] flex flex-col items-center justify-center md:flex md:h-[54px] md:flex-row',
              { block: showMobileMenu },
            )}
          >
            <NavGroups />
            <NavPromo />
          </div>
        </nav>
      </div>
    </div>
  );
}
